import React, { useEffect, useMemo, useState } from 'react';
import { get, ref, runTransaction, update } from 'firebase/database';
import { getDownloadURL, listAll, ref as storageRef } from 'firebase/storage';
import { auth, database, storage } from '../firebase';

type QueueTarget = 'preview' | 'global' | 'standard';
type ToastType = 'neutral' | 'success' | 'error';

const appendProjectToQueue = async (path: string, projectId: string) => {
  await runTransaction(ref(database, path), (current) => {
    if (Array.isArray(current)) {
      if (current.includes(projectId)) {
        return current;
      }
      return [...current, projectId];
    }

    if (!current || typeof current !== 'object') {
      return [projectId];
    }

    const values = Object.values(current as Record<string, unknown>).map((value) => String(value ?? '').trim());
    if (values.includes(projectId)) {
      return current;
    }
    const next = { ...(current as Record<string, unknown>) };
    const numericKeys = Object.keys(next)
      .map((key) => Number(key))
      .filter((key) => Number.isFinite(key));
    const nextKey = numericKeys.length ? String(Math.max(...numericKeys) + 1) : '0';
    next[nextKey] = projectId;
    return next;
  });
};

const containsProjectId = (value: unknown, projectId: string): boolean => {
  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim() === projectId);
  }
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Object.values(value as Record<string, unknown>).some(
    (entry) => String(entry ?? '').trim() === projectId
  );
};

const isRunningProject = (runNode: unknown, projectId: string) => {
  if (!runNode || typeof runNode !== 'object') {
    return false;
  }
  return Object.keys(runNode as Record<string, unknown>).some((key) => key.trim() === projectId);
};

const previewFolderPattern = /^Preview_(\d+)$/;

const parsePreviewFolderId = (folderName: string) => {
  const match = folderName.match(previewFolderPattern);
  if (!match) {
    return null;
  }
  const id = Number(match[1]);
  if (!Number.isInteger(id) || id < 1) {
    return null;
  }
  return id;
};

const hasPreviewMarker = async (projectId: string, previewId: number) => {
  const markerPath = `${projectId}/Preview_${previewId}/JSONS/preview.json`;
  try {
    await getDownloadURL(storageRef(storage, markerPath));
    return true;
  } catch (error) {
    return false;
  }
};

const loadPreviewIds = async (projectId: string) => {
  try {
    const listResult = await listAll(storageRef(storage, projectId));
    const candidates = listResult.prefixes
      .map((item) => ({ name: item.name, id: parsePreviewFolderId(item.name) }))
      .filter((item): item is { name: string; id: number } => item.id !== null);

    const checks = await Promise.all(
      candidates.map(async (item) => ({
        id: item.id,
        exists: await hasPreviewMarker(projectId, item.id),
      }))
    );

    return checks
      .filter((item) => item.exists)
      .map((item) => item.id)
      .sort((a, b) => a - b);
  } catch (error) {
    return [] as number[];
  }
};

function EnqueueProjectSection() {
  const [projectId, setProjectId] = useState('');
  const [queueTarget, setQueueTarget] = useState<QueueTarget>('preview');
  const [selectedPreview, setSelectedPreview] = useState('');
  const [availablePreviews, setAvailablePreviews] = useState<number[]>([]);
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(false);
  const [shouldSendEmail, setShouldSendEmail] = useState(false);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState<ToastType>('neutral');
  const [isEnqueueing, setIsEnqueueing] = useState(false);
  const needsPreviewSelection = queueTarget === 'global' || queueTarget === 'standard';
  const normalizedProjectId = projectId.trim();
  const hasValidPreviewSelection = useMemo(
    () => availablePreviews.includes(Number(selectedPreview)),
    [availablePreviews, selectedPreview]
  );
  const disableEnqueue =
    isEnqueueing ||
    (needsPreviewSelection &&
      (!normalizedProjectId || isLoadingPreviews || availablePreviews.length === 0 || !hasValidPreviewSelection));

  useEffect(() => {
    let isMounted = true;
    if (!needsPreviewSelection || !normalizedProjectId) {
      setAvailablePreviews([]);
      setSelectedPreview('');
      setIsLoadingPreviews(false);
      return () => {
        isMounted = false;
      };
    }

    const run = async () => {
      setIsLoadingPreviews(true);
      const ids = await loadPreviewIds(normalizedProjectId);
      if (!isMounted) {
        return;
      }
      setAvailablePreviews(ids);
      setSelectedPreview((prev) => {
        if (ids.includes(Number(prev))) {
          return prev;
        }
        return ids.length ? String(ids[0]) : '';
      });
      setIsLoadingPreviews(false);
    };
    void run();

    return () => {
      isMounted = false;
    };
  }, [needsPreviewSelection, normalizedProjectId]);

  const handleEnqueue = async () => {
    const user = auth.currentUser;
    if (!user) {
      setStatus('Inicia sesión para encolar proyecto.');
      setStatusType('error');
      return;
    }

    if (!normalizedProjectId) {
      setStatus('Ingresa un Project ID válido.');
      setStatusType('error');
      return;
    }
    if (needsPreviewSelection) {
      if (!hasValidPreviewSelection) {
        setStatus('Selecciona un preview válido para encolar Global/Standard.');
        setStatusType('error');
        return;
      }
      const previewId = Number(selectedPreview);
      if (!(await hasPreviewMarker(normalizedProjectId, previewId))) {
        setStatus(`No existe Preview_${previewId} con JSONS/preview.json.`);
        setStatusType('error');
        return;
      }
    }

    setIsEnqueueing(true);
    setStatus('Validando proyecto y colas...');
    setStatusType('neutral');

    try {
      const [projectSnapshot, queueSnapshot, queuePreviewSnapshot] = await Promise.all([
        get(ref(database, `Projects/${normalizedProjectId}`)),
        get(ref(database, 'Queue')),
        get(ref(database, 'QueuePreview')),
      ]);

      if (!projectSnapshot.exists()) {
        setStatus('El proyecto no existe.');
        setStatusType('error');
        return;
      }

      const queueValue = (queueSnapshot.val() ?? {}) as Record<string, unknown>;
      const queuePreviewValue = (queuePreviewSnapshot.val() ?? {}) as Record<string, unknown>;

      const existsInAnyQueue =
        containsProjectId(queueValue.projectsGlobal, normalizedProjectId) ||
        containsProjectId(queueValue.projectsStandard, normalizedProjectId) ||
        containsProjectId(queuePreviewValue.projects, normalizedProjectId);

      const runningInAnyQueue =
        isRunningProject(queueValue.run, normalizedProjectId) ||
        isRunningProject(queuePreviewValue.run, normalizedProjectId);

      if (existsInAnyQueue || runningInAnyQueue) {
        setStatus('No se puede encolar: el proyecto ya está en cola o corriendo.');
        setStatusType('error');
        return;
      }

      const updates: Record<string, unknown> = {};
      if (queueTarget === 'preview') {
        await appendProjectToQueue('QueuePreview/projects', normalizedProjectId);
        if (!shouldSendEmail) {
          updates[`Projects/${normalizedProjectId}/preview_email_sent`] = true;
        }
      } else {
        const isGlobal = queueTarget === 'global';
        const previewId = Number(selectedPreview);
        await appendProjectToQueue(
          isGlobal ? 'Queue/projectsGlobal' : 'Queue/projectsStandard',
          normalizedProjectId
        );
        updates[`Projects/${normalizedProjectId}/type`] = isGlobal ? 'Global' : 'Standard';
        updates[`Projects/${normalizedProjectId}/type_price`] = isGlobal
          ? 'Admin_Global'
          : 'Admin_Standard';
        updates[`ProjectType/${isGlobal ? 'Admin_Global' : 'Admin_Standard'}/${normalizedProjectId}`] = true;
        updates[`Projects/${normalizedProjectId}/selected_preview`] = previewId;
        if (!shouldSendEmail) {
          updates[`Projects/${normalizedProjectId}/email_sent`] = true;
        }
      }

      if (Object.keys(updates).length) {
        await update(ref(database), updates);
      }

      setStatus('Proyecto encolado correctamente.');
      setStatusType('success');
    } catch (error) {
      setStatus('Error al encolar proyecto.');
      setStatusType('error');
    } finally {
      setIsEnqueueing(false);
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Encolar proyecto</h2>
      </div>

      <div className="prompt-ready">
        <label className="field">
          <span>Project ID</span>
          <input
            className="table-input"
            type="text"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            placeholder="ID del proyecto"
          />
        </label>

        <div className="field">
          <span>Cola</span>
          <div className="panel-actions-inline">
            <label>
              <input
                type="radio"
                name="enqueue-target"
                value="preview"
                checked={queueTarget === 'preview'}
                onChange={() => setQueueTarget('preview')}
              />{' '}
              Preview
            </label>
            <label>
              <input
                type="radio"
                name="enqueue-target"
                value="global"
                checked={queueTarget === 'global'}
                onChange={() => setQueueTarget('global')}
              />{' '}
              Global
            </label>
            <label>
              <input
                type="radio"
                name="enqueue-target"
                value="standard"
                checked={queueTarget === 'standard'}
                onChange={() => setQueueTarget('standard')}
              />{' '}
              Standard
            </label>
          </div>
        </div>

        {needsPreviewSelection ? (
          <label className="field">
            <span>Preview seleccionado</span>
            <select
              className="table-input"
              value={selectedPreview}
              onChange={(event) => setSelectedPreview(event.target.value)}
              disabled={isLoadingPreviews || !availablePreviews.length}
            >
              {!availablePreviews.length ? (
                <option value="">{isLoadingPreviews ? 'Cargando previews...' : 'Sin previews disponibles'}</option>
              ) : null}
              {availablePreviews.map((previewId) => (
                <option key={previewId} value={String(previewId)}>
                  Preview_{previewId}
                </option>
              ))}
            </select>
            {!normalizedProjectId ? <div className="hint">Ingresa Project ID para cargar previews.</div> : null}
            {normalizedProjectId && !isLoadingPreviews && !availablePreviews.length ? (
              <div className="hint">No hay carpetas Preview_&#123;n&#125;/JSONS/preview.json válidas.</div>
            ) : null}
          </label>
        ) : null}

        <label className="field">
          <span>Enviar email</span>
          <div className="panel-actions-inline">
            <input
              type="checkbox"
              checked={shouldSendEmail}
              onChange={(event) => setShouldSendEmail(event.target.checked)}
            />
          </div>
        </label>

        <div className="panel-actions-inline">
          <button className="primary-button" type="button" onClick={handleEnqueue} disabled={disableEnqueue}>
            {isEnqueueing ? 'Encolando...' : 'Encolar proyecto'}
          </button>
        </div>
        {status ? <div className={`inline-toast inline-toast-${statusType}`}>{status}</div> : null}
      </div>
    </div>
  );
}

export default EnqueueProjectSection;
